
import { supabase } from '@/lib/supabase';

export interface RegexPattern {
  id: string;
  name: string;
  pattern: string;
  description?: string;
  is_active: boolean;
  metadata?: {
    type?: string;
    priority?: number;
    example?: string;
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

export const fetchRegexPatterns = async (): Promise<RegexPattern[]> => {
  try {
    const { data, error } = await supabase
      .from('ai_regex_patterns')
      .select('*')
      .order('metadata->priority', { ascending: true });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching regex patterns:', error);
    throw error;
  }
};

export const fetchActiveRegexPatterns = async (): Promise<RegexPattern[]> => {
  try {
    const { data, error } = await supabase
      .from('ai_regex_patterns')
      .select('*')
      .eq('is_active', true)
      .order('metadata->priority', { ascending: true });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching active regex patterns:', error);
    throw error;
  }
};

export const getRegexPatternById = async (id: string): Promise<RegexPattern | null> => {
  try {
    const { data, error } = await supabase
      .from('ai_regex_patterns')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching regex pattern by ID:', error);
    throw error;
  }
};

export const createRegexPattern = async (pattern: Omit<RegexPattern, 'id' | 'created_at' | 'updated_at'>): Promise<RegexPattern> => {
  try {
    // Validate regex pattern before saving
    try {
      new RegExp(pattern.pattern);
    } catch (e) {
      throw new Error('Invalid regex pattern: ' + e.message);
    }
    
    const { data, error } = await supabase
      .from('ai_regex_patterns')
      .insert(pattern)
      .select()
      .single();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating regex pattern:', error);
    throw error;
  }
};

export const updateRegexPattern = async (id: string, updates: Partial<RegexPattern>): Promise<RegexPattern> => {
  try {
    // If pattern is being updated, validate it
    if (updates.pattern) {
      try {
        new RegExp(updates.pattern);
      } catch (e) {
        throw new Error('Invalid regex pattern: ' + e.message);
      }
    }
    
    const { data, error } = await supabase
      .from('ai_regex_patterns')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating regex pattern:', error);
    throw error;
  }
};

export const deleteRegexPattern = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('ai_regex_patterns')
      .delete()
      .eq('id', id);
      
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting regex pattern:', error);
    throw error;
  }
};

export const testRegexPattern = (pattern: string, testString: string): { matches: boolean; groups: string[] } => {
  try {
    const regex = new RegExp(pattern, 's');
    const match = testString.match(regex);
    
    if (!match) {
      return { matches: false, groups: [] };
    }
    
    // Extract capture groups, filter out undefined values
    const groups = match.slice(1)
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map(item => item.trim());
      
    return {
      matches: true,
      groups
    };
  } catch (error) {
    console.error('Error testing regex pattern:', error);
    return { matches: false, groups: [] };
  }
};
